/**
 * Video Helper Utilities
 * Utilities for video validation, duration detection, and processing
 */

export interface VideoValidationResult {
  valid: boolean;
  duration?: number;
  error?: string;
}

/**
 * Get duration of a video file in seconds
 */
export async function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    
    video.src = URL.createObjectURL(file);
  });
}

/**
 * Validate video file for loop posting
 * Max duration: 12 seconds
 */
export async function validateVideoForLoop(file: File): Promise<VideoValidationResult> {
  const MAX_DURATION = 12; // seconds
  
  try {
    // Check if it's a video file
    if (!file.type.startsWith('video/')) {
      return {
        valid: false,
        error: 'File must be a video'
      };
    }
    
    // Get video duration
    const duration = await getVideoDuration(file);
    
    // Check duration limit
    if (duration > MAX_DURATION) {
      return {
        valid: false,
        duration,
        error: `Video too long (${duration.toFixed(1)}s). Please trim to ${MAX_DURATION} seconds or less.`
      };
    }
    
    return {
      valid: true,
      duration
    };
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Failed to validate video'
    };
  }
}

/**
 * Format duration in seconds to MM:SS format
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Check if file is a video
 */
export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}
